// modal.service.ts
import { inject, Injectable } from '@angular/core';
import { ParamMap, Router } from '@angular/router';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { StudyService } from '@nx-superprep/backend/firebase';
import { log } from '@nx-superprep/utils';
import { ModalComponentType } from './modal.type'
import { ComponentRegistryService } from './components-registry.service'
import { BreakpointService } from './breakpoint.service'

@Injectable({ providedIn: 'root' })
export class ModalService {
  private readonly modalService = inject(BsModalService)
  private readonly router = inject(Router);
  private readonly studyService = inject(StudyService)
  private readonly breakpointService = inject(BreakpointService)
  private readonly registry = inject(ComponentRegistryService)

  isInFrame = false

  constructor() {
    this.isInFrame = window.self !== window.top;
  }

  get isWeb() {
    return this.breakpointService.isWeb || this.isInFrame
  }

  openModal(route: string, id: string): BsModalRef | undefined {
    const config = this.getModalConfig(route, id);
    const componentType = this.getComponentType(route);
    const componentClass = this.registry.getComponent(componentType);

    if (!componentClass) return undefined;

    const bsModalRef = this.modalService.show(componentClass, config) as BsModalRef;
    this.setupModalHandlers(bsModalRef, route, id);
    this.styleModalParent(config.class ?? '', route);
    return bsModalRef;
  }

  setupModalHandlers(bsModalRef: BsModalRef, route: string, id: string): void {
    // Setup modal handlers
    if (bsModalRef?.content && 'close' in bsModalRef.content) {
      bsModalRef.content.close = (reason: string) =>
        this.handleModalClose(reason, id, bsModalRef);
    }
  }

  handleModalClose(reason: string, id: string, modalRef: BsModalRef): void {
    const navigationMap = this.buildNavigationMap(id);
    const path = navigationMap[reason] || ['/'];
    log.debug(`Navigating to ${path} from ${id} due to ${reason}`);

    modalRef.hide();

    if (reason === 'complete') {
      this.openModal('complete', id);
    } else if (reason === 'pause') {
      this.openModal('pause', id);
    } else if (reason === 'settings') {
      this.openModal('settings', id);
    } else if (reason === 'continue') {
      const nextModuleId = this.studyService?.getNextModuleIdSync(id);
      if (nextModuleId) {
        this.openModal('study', nextModuleId);
      } else {
        this.router.navigate(['/'], { replaceUrl: true, skipLocationChange: true });
      }
    } else {
      if (reason === 'exit' && !this.studyService?.activeLesson) { return }
      this.router.navigate(path, { replaceUrl: true, skipLocationChange: true });
    }
  }

  private buildNavigationMap(id: string): Record<string, string[]> {
    const isRegular = this.studyService?.activeLesson && !this.studyService.activeLesson.module.isFinished;
    return {
      'pause': ['/pause', id],
      'complete': ['/complete', id],
      'exit': isRegular ? ['/pause', id] : [''],
      'continue': this.getNextModulePath(id),
      'settings': ['/settings', id],
      'quit': ['']
    };
  }

  private getNextModulePath(currentModuleId: string): string[] {
    const nextModuleId = this.studyService?.getNextModuleIdSync(currentModuleId);
    return nextModuleId ? ['/study', nextModuleId] : ['/'];
  }

  private getComponentType(route: string): ModalComponentType {
    const componentTypeMap: Record<string, ModalComponentType> = {
      'study': 'LessonStudyComponent',
      'quiz': 'LessonStudyComponent',
      'practice': 'LessonStudyComponent',
      'sample': 'LessonStudyComponent',
      'done': 'LessonCompleteComponent',
      'complete': 'LessonCompleteComponent',
      'status': 'LessonDetailComponent',
      'legal': 'LegalComponent',
      'pause': 'LessonPauseComponent',
      'settings': 'SettingsComponent',
      'login': 'AuthComponent',
      'register': 'AuthComponent',
      'auth_transfer': 'AuthTransferComponent',
      'auth_transfer_prep': 'AuthTransferComponent',
      'feedback': 'FeedbackComponent',
    };
    return componentTypeMap[route];
  }

  private getModalConfig(route: string, id: string): ModalOptions<unknown> {
    const isFullHeight = !this.isWeb || ['legal', 'pause', 'settings'].includes(route);
    const isModal = this.isWeb && ['settings', 'login', 'register', 'feedback'].includes(route);
    const isContained = this.isWeb && ['complete', 'pause', 'feedback'].includes(route);
    const modalSize = isModal ? (['login','register', 'feedback'].includes(route) ? 'modal-md' : 'modal-lg') : 'takeover-modal';

    // Add exceptions where noscrollbar shouldn't be applied
    const excludeScrollbar = ['legal', 'settings', 'feedback'].includes(route);
    const modalScroll = isContained && !excludeScrollbar ? 'noscrollbar' : '';

    const modalHeight = isFullHeight ? 'vh-100' : '';

    return {
      animated: true,
      keyboard: isModal,
      backdrop: isModal,
      ignoreBackdropClick: true,
      class: `${modalSize} ${modalScroll} ${modalHeight}`.trim(),
      initialState: this.createInitialState(route, id),
    };
  }

    private createInitialState(route: string, id: string, queryParamMap?: ParamMap) {
    return {
      name: route,
      moduleId: id,
      action: ['study', 'quiz', 'practice'].includes(route) ? route : undefined,
      legal: route === 'legal' ? id : undefined,
      options: ['login','register'].includes(route) ? { state: route, ...queryParamMap } : undefined,
    }
  }

  /**
   * Style the modal parent element
   * @param modalSize - Size class of the modal
   */
  private styleModalParent(modalSize: string, route: string) {
    const bsModalParent = document?.getElementsByClassName(modalSize)[0]?.parentElement;
    if (bsModalParent) {
      // Only apply noscrollbar if not in excluded routes
      if (!['legal', 'settings', 'feedback'].includes(route)) {
        bsModalParent.classList.toggle('noscrollbar', true);
      }
      bsModalParent.style.backgroundColor = 'var(--dl-color-background-primary-700)';
    }
  }
}
